// ── Clickstream capture — "which buttons get clicked, what people do on the
// page" (Lisa, 2026-07-19). MVP scope: CAPTURE + STORE only, via one
// delegated listener wired ONCE at the app root (see App.tsx's initClickstream
// call). A founder dashboard is a later phase; this just feeds it data
// (backend: src/shared/clickstream.ts + POST /events/clicks in portalApi).
//
// POPIA posture: labels + a SANITIZED route only — no input values, no free
// text, no query strings, nothing beyond the clicked control's own resolved
// label. sanitizeRoute() below replaces any id-shaped path segment
// (numeric/UUID/prefixed-entity-id, raw or percent-encoded) with ':id' before
// it ever leaves the browser, so raw clan/discord/match ids never pile up in
// stored routes. This is product analytics on control usage, not
// surveillance of what a user typed or read.
//
// Fail-silent by design: a broken or slow /events/clicks must never surface
// to the user or spam devtools. One console.debug on the FIRST flush
// failure ever, then silence — paired with an exponential backoff on the
// flush interval (5s -> 10s -> 20s ... capped at 2min, reset to 5s the
// moment a flush succeeds again) so a dead endpoint doesn't get hammered.
// Dropped events on a failed flush are NOT re-queued — for telemetry this
// tier of data loss during an outage is an acceptable trade against letting
// the queue grow unbounded while offline.
//
// Auth/transport choice (read this before changing it): sendBeacon() cannot
// set the Authorization header, which is how every other portalApi call
// authenticates (see lib/api.ts's apiFetch). Rather than fork the flush path
// into "beacon on unload, fetch elsewhere" — two code paths to keep in sync
// for zero real benefit — every flush (interval, count-threshold, AND the
// visibilitychange->hidden trigger) uses the SAME fetch(..., {keepalive:
// true}) call. `keepalive` is exactly what sendBeacon uses under the hood
// for the unload case, and unlike sendBeacon it lets us attach the same
// Bearer token apiFetch does, so anonymous/authed actor stamping stays
// correct on every trigger, not just the common ones. Simplest correct
// choice — one path, not two.

import { getStoredToken } from './auth';

export type ClickKind = 'button' | 'link' | 'other';

export interface ClickstreamEvent {
  ts: number;
  route: string;
  label: string;
  kind: ClickKind;
}

// Mirrors the backend's caps (src/shared/clickstream.ts) — capped smaller
// here so a truncated-but-still-meaningful label always survives the
// backend's own (larger) MAX_LABEL_LEN=64 check untouched.
const MAX_LABEL_LEN = 40;

const FLUSH_AT_COUNT = 20;
const BASE_INTERVAL_MS = 5_000;
const MAX_BACKOFF_MS = 120_000;

const CLICK_SELECTOR = 'button, a, [role="button"], summary, input[type="submit"]';

// ── Route sanitization ───────────────────────────────────────────────────────

const NUMERIC_ID = /^\d{5,}$/; // Discord snowflakes (17-19 digits) and similar numeric ids
const UUID_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Canonical prefixed store ids (CLAN#foo, ORG#anvil, COMP#xyz, MATCH#..., USER#...)
// — never expected raw in a path (memory: the '#' fragment trap means these
// get percent-encoded, e.g. CLAN%23foo, if they ever land in a URL) but
// checked post-decode either way.
const PREFIXED_ID = /^[A-Z][A-Z_]*#/;

function isIdShapedSegment(decoded: string): boolean {
  return NUMERIC_ID.test(decoded) || UUID_ID.test(decoded) || PREFIXED_ID.test(decoded);
}

/**
 * Replace any id-shaped path segment (numeric, UUID, or a canonical
 * PREFIX#-style id — decoded first so a percent-encoded id is caught too)
 * with ':id'. Ordinary human slugs (clan names, org slugs, "My Clan" with a
 * space, etc.) pass through unchanged — only segments that actually LOOK
 * like an id get collapsed, so route granularity isn't lost for real slugs.
 */
export function sanitizeRoute(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (seg === '') return seg;
      let decoded = seg;
      try {
        decoded = decodeURIComponent(seg);
      } catch {
        return ':id'; // malformed percent-encoding — treat as opaque, never store raw
      }
      return isIdShapedSegment(decoded) ? ':id' : seg;
    })
    .join('/');
}

// ── Label / kind resolution ──────────────────────────────────────────────────

/** data-track > aria-label > trimmed textContent, capped at MAX_LABEL_LEN. */
export function resolveLabel(el: Element): string {
  const dataTrack = el.getAttribute('data-track');
  if (dataTrack && dataTrack.trim()) return dataTrack.trim().slice(0, MAX_LABEL_LEN);

  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim().slice(0, MAX_LABEL_LEN);

  const text = (el.textContent || '').trim().replace(/\s+/g, ' ');
  return text.slice(0, MAX_LABEL_LEN);
}

/** Real anchors are 'link'; every other matched control (button/summary/input[submit]/role=button) is 'button'. */
export function resolveKind(el: Element): ClickKind {
  const tag = el.tagName.toLowerCase();
  if (tag === 'a') return 'link';
  if (tag === 'button' || tag === 'summary' || tag === 'input') return 'button';
  if (el.getAttribute('role') === 'button') return 'button';
  return 'other'; // unreachable given CLICK_SELECTOR, kept for type-safety
}

// ── Batching + flush ──────────────────────────────────────────────────────────

let queue: ClickstreamEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let currentIntervalMs = BASE_INTERVAL_MS;
let hasLoggedFailure = false;
let initialized = false;

function scheduleTimer(intervalMs: number): void {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(() => { void flush(); }, intervalMs);
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const events = queue;
  queue = [];

  try {
    const token = getStoredToken();
    await fetch('/portal/events/clicks', {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events }),
    });
    if (currentIntervalMs !== BASE_INTERVAL_MS) {
      currentIntervalMs = BASE_INTERVAL_MS;
      scheduleTimer(currentIntervalMs);
    }
  } catch (err) {
    if (!hasLoggedFailure) {
      hasLoggedFailure = true;
      // eslint-disable-next-line no-console
      console.debug('[clickstream] flush failed — will keep retrying quietly with backoff', err);
    }
    currentIntervalMs = Math.min(currentIntervalMs * 2, MAX_BACKOFF_MS);
    scheduleTimer(currentIntervalMs);
  }
}

function enqueue(evt: ClickstreamEvent): void {
  queue.push(evt);
  if (queue.length >= FLUSH_AT_COUNT) void flush();
}

function handleClick(e: MouseEvent): void {
  try {
    const target = e.target as Element | null;
    if (!target) return;
    const matched = target.closest(CLICK_SELECTOR);
    if (!matched) return;

    const label = resolveLabel(matched);
    if (!label) return; // nothing meaningful to record (e.g. an icon-only button with no label at all)

    enqueue({
      ts: Date.now(),
      route: sanitizeRoute(window.location.pathname),
      label,
      kind: resolveKind(matched),
    });
  } catch {
    // Fail-silent — a DOM read glitch must never break the click itself.
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') void flush();
}

/**
 * Wire the single delegated click listener + batching timers. Call ONCE at
 * the app root (App.tsx). Idempotent — safe to call more than once (e.g.
 * React StrictMode's double-invoked effects in dev) and safe under SSR/tests
 * where `document` may not exist.
 */
export function initClickstream(): void {
  if (initialized) return;
  if (typeof document === 'undefined') return;
  initialized = true;

  // Capture phase: catches the click even if a component's own handler
  // calls stopPropagation() before it would otherwise bubble to us.
  document.addEventListener('click', handleClick, { capture: true });
  document.addEventListener('visibilitychange', handleVisibilityChange);
  scheduleTimer(currentIntervalMs);
}

/**
 * Test-only: reset the in-memory batching state (queue, backoff interval,
 * failure-log flag) between test cases WITHOUT touching the singleton init
 * guard or re-attaching listeners — initClickstream() stays a call-once-per-
 * page-load singleton in real usage; tests just need a clean queue/timer
 * between cases in the same file. Not called anywhere in production code.
 */
export function __resetClickstreamStateForTests(): void {
  queue = [];
  hasLoggedFailure = false;
  currentIntervalMs = BASE_INTERVAL_MS;
  if (initialized) scheduleTimer(currentIntervalMs);
}
